import { Grid, Input, Button, Loader } from '@scuf/common';
import { useTranslation } from 'react-i18next';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { RmaLiteMf } from 'resources/atoms/rmaManufaturaAtom';
import LabelDescription from '../../../../components/labelDescription';
import Alert from 'components/alert';
import { RmaModelLiteMf } from 'models';
import React, { useEffect, useState } from 'react';
import useFetch from 'helpers/hooks/useFetch';
import { RMAMANUFATURA, REPAIR_CENTER, COMPANY_MANAGER, rmaState } from 'resources';
import { CompanyDTO } from 'models/userManager/addCustomer';
import { rmaItemsMf } from 'models/rmamanufatura/detailsRma';
import useUser from 'helpers/hooks/useUser';

const STATUS_AGUARDANDO_CLIENTE = 'AGUARDANDO INFORMAÇÃO DO CLIENTE';
const STATUS_AGUARDANDO_CLIENTE_ID = 54;
const STATUS_APROVACAO_CX_ID = 53;
const STATUS_APROVACAO_CX_NAME = 'AGUARDANDO APROVAÇÃO DE CX';

type AddressManufacturingDTO = {
    RmaManufacturingId: number;
    Cep: string;
    Rua: string;
    Estado: string;
    Numero: string;
    Cidade: string;
    Bairro: string;
    Complemento: string;
};

const AddressAccordion: React.FC = () => {
    const { t } = useTranslation(['user', 'rmamanufatura']);
    const rmaDetail = useRecoilValue<RmaModelLiteMf>(RmaLiteMf);
    const setRmaDetail = useSetRecoilState<RmaModelLiteMf>(RmaLiteMf);
    const [zipCode, setZipCode] = useState(rmaDetail?.address?.zipCode || '');
    const [street, setStreet] = useState(rmaDetail?.address?.street || '');
    const [state, setState] = useState(rmaDetail?.address?.state || '');
    const [number, setNumber] = useState(rmaDetail?.addressNumber ? rmaDetail.addressNumber.toString() : '');
    const [city, setCity] = useState(rmaDetail?.address?.city || '');
    const [complement, setComplement] = useState(rmaDetail?.addressComplement || '');
    const [neighborhood, setNeighborhood] = useState(rmaDetail?.address?.neighborhood || '');
    const [addressLoaded, setAddressLoaded] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const { user } = useUser();

    const hasAddress =
        !!rmaDetail?.addressId ||
        (rmaDetail?.address?.zipCode && rmaDetail?.address?.street && rmaDetail?.address?.city && rmaDetail?.address?.state);

    const showForm =
        !hasAddress &&
        (rmaDetail?.status?.id === STATUS_AGUARDANDO_CLIENTE_ID || rmaDetail?.status?.name === STATUS_AGUARDANDO_CLIENTE);

    const [response, createAddress, loading] = useFetch<AddressManufacturingDTO>({
        url: RMAMANUFATURA.createAddress,
        method: 'POST'
    });

    const [_, sendUpdateStatusManufacturingEmail, loadingStatusEmail] = useFetch<
        { rmaId: number; statusId: any; email: string; cnpj: string; ssoId: string },
        { error: boolean; message: string }
    >({
        url: RMAMANUFATURA.updateStatusManufacturingSendEmail,
        method: 'POST',
        onError: 'Change',
        onSuccess: 'Change'
    });

    const [responseCompany, getCompanyById, loadingCompany] = useFetch<{ id: number | undefined }, CompanyDTO>({
        url: COMPANY_MANAGER.getCompanyById,
        method: 'GET',
        onError: 'EmailError'
    });

    const [, ProcessingRmaManufacturing] = useFetch<{
        id: number;
        email: any;
        situation: string;
        shipping: string;
        nextStatus: number;
        statusId: number;
        observation: string;
        rmaItemsManufacturing: rmaItemsMf[];
        ssoId: string;
    }>({
        url: RMAMANUFATURA.processingRmaManufacturing,
        method: 'POST',
        onError: 'Erro ao alterar situação'
    });

    useEffect(() => {
        if (rmaDetail?.id && rmaDetail?.addressId) {
            fetch(`${RMAMANUFATURA.getAddress}?rmaManufacturingId=${rmaDetail.id}`)
                .then(res => res.json())
                .then(data => {
                    if (data && data.cep) {
                        setRmaDetail(old => ({
                            ...old,
                            address: {
                                zipCode: data.cep,
                                street: data.rua,
                                state: data.estado,
                                city: data.cidade,
                                neighborhood: data.bairro
                            },
                            addressNumber: data.numero,
                            addressComplement: data.complemento
                        }));
                        setAddressLoaded(true);
                    }
                })
                .catch(error => console.error('Erro:', error))
                .finally(() => {
                    setAddressLoaded(true);
                });
        } else {
            setAddressLoaded(true);
        }
    }, [rmaDetail?.id, rmaDetail?.addressId]);

    const handleSubmit = async () => {
        if (!zipCode || !street || !state || !city || !neighborhood || !number) {
            alert(t('rmamanufatura:translation.fillRequiredFields'));
            return;
        }
        setSubmitting(true);

        await createAddress(
            {
                RmaManufacturingId: rmaDetail.id,
                Cep: zipCode,
                Rua: street,
                Estado: state,
                Numero: number,
                Cidade: city,
                Bairro: neighborhood,
                Complemento: complement
            },
            async (res: any) => {
                await getCompanyById({ id: rmaDetail.companyId });
                await ProcessingRmaManufacturing({
                    id: rmaDetail.id,
                    email: responseCompany?.data?.email ?? '',
                    situation: rmaDetail.situation,
                    shipping: rmaDetail.shipping,
                    nextStatus: STATUS_APROVACAO_CX_ID,
                    statusId: STATUS_APROVACAO_CX_ID,
                    observation: 'Endereço preenchido pelo cliente',
                    rmaItemsManufacturing: rmaDetail.rmaItemsManufacturing,
                    ssoId: user?.ssoid || '-'
                });

                setRmaDetail(old => ({
                    ...old,
                    statusId: STATUS_APROVACAO_CX_ID,
                    status: { name: STATUS_APROVACAO_CX_NAME, id: STATUS_APROVACAO_CX_ID },
                    address: {
                        zipCode,
                        street,
                        state,
                        city,
                        neighborhood
                    },
                    addressNumber: number,
                    addressComplement: complement,
                    addressId: res?.data?.id || old.addressId
                }));
                setSubmitting(false);
                window.location.reload();
            }
        );
    };

    if (!addressLoaded && rmaDetail?.id) {
        return <Loader text={t('common:actions.Loading')} />;
    }

    return (
        <>
            {showForm ? (
                <>
                    <Alert text={t('rmamanufatura:translation.fillAddressToProgress')} />
                    <Grid>
                        <Grid.Row>
                            <Grid.Column className="grid-column" width={6} sWidth={12}>
                                <Input
                                    value={zipCode}
                                    onChange={setZipCode}
                                    label={t('address.ZipCode')}
                                    placeholder={t('address.ZipCode')}
                                    indicator="required"
                                />
                                <Input
                                    value={street}
                                    onChange={setStreet}
                                    label={t('address.Street')}
                                    placeholder={t('address.Street')}
                                    indicator="required"
                                />
                                <Input
                                    value={state}
                                    onChange={setState}
                                    label={t('address.State')}
                                    placeholder={t('address.State')}
                                    indicator="required"
                                />
                                <Input
                                    value={number}
                                    onChange={setNumber}
                                    label={t('address.Number')}
                                    placeholder={t('address.Number')}
                                    indicator="required"
                                />
                            </Grid.Column>
                            <Grid.Column className="grid-column" width={6} sWidth={12}>
                                <Input
                                    value={city}
                                    onChange={setCity}
                                    label={t('address.City')}
                                    placeholder={t('address.City')}
                                    indicator="required"
                                />
                                <Input
                                    value={neighborhood}
                                    onChange={setNeighborhood}
                                    label={t('address.Neighborhood')}
                                    placeholder={t('address.Neighborhood')}
                                    indicator="required"
                                />
                                <Input
                                    value={complement}
                                    onChange={setComplement}
                                    label={t('address.Complement')}
                                    placeholder={t('address.Complement')}
                                />
                            </Grid.Column>
                        </Grid.Row>
                        <Grid.Row>
                            <Grid.Column>
                                <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                                    <Button
                                        content={t('Enviar')}
                                        loading={submitting}
                                        disabled={submitting}
                                        onClick={handleSubmit}
                                    />
                                </div>
                            </Grid.Column>
                        </Grid.Row>
                    </Grid>
                </>
            ) : (
                <Grid>
                    <Grid.Row>
                        <Grid.Column className="grid-column" width={6} sWidth={12}>
                            <LabelDescription value={rmaDetail?.address?.zipCode} title={t('address.ZipCode')} />
                            <LabelDescription value={rmaDetail?.address?.street} title={t('address.Street')} />
                            <LabelDescription value={rmaDetail?.address?.state} title={t('address.State')} />
                            <LabelDescription value={rmaDetail?.addressNumber?.toString()} title={t('address.Number')} />
                        </Grid.Column>
                        <Grid.Column className="grid-column" width={6} sWidth={12}>
                            <LabelDescription value={rmaDetail?.address?.city} title={t('address.City')} />
                            <LabelDescription value={rmaDetail?.address?.neighborhood} title={t('address.Neighborhood')} />
                            <LabelDescription value={rmaDetail?.addressComplement || '-'} title={t('address.Complement')} />
                        </Grid.Column>
                    </Grid.Row>
                </Grid>
            )}
        </>
    );
};

export default AddressAccordion;
